import { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { useLazyQuery } from '@apollo/client';
import { QUERY_DIMENSIONS_BY_SKU } from '../graphql/queries';
import { captureException } from '@sentry/react';
import { skuCatalogPatch } from '../utilities/skuHydration';

export const useItemDynamicFields = (item, modifyItem, shipperId, dimensionOverrides, serviceTimeTouched) => {
    const [getDimensions, { data: cached }] = useLazyQuery(QUERY_DIMENSIONS_BY_SKU, {
        // A cached row outlives a fix made in the SKU Warehouse from another tab, and whatever this
        // row puts on the order is written back to the SKU when the order is saved.
        fetchPolicy: 'network-only',
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    //dimensions
    useEffect(() => {
        const cachedDimensions = cached?.result?.[0] || {};
        modifyItem({
            length: dimensionOverrides.length || cachedDimensions.length,
            width: dimensionOverrides.width || cachedDimensions.width,
            height: dimensionOverrides.height || cachedDimensions.height,
            weight: dimensionOverrides.weight || cachedDimensions.weight,
            cubes_per_unit: dimensionOverrides.cubes || cachedDimensions.cubes,
        });
    }, [dimensionOverrides, cached]);

    //service_time
    useEffect(() => {
        if (serviceTimeTouched) return;
        const cachedDimensions = cached?.result?.[0] || {};
        if (cachedDimensions.service_time !== undefined) {
            modifyItem({ service_time: cachedDimensions.service_time });
        }
    }, [cached]);

    //sku catalog fields
    useEffect(() => {
        const patch = skuCatalogPatch(item, cached?.result?.[0]);
        if (Object.keys(patch).length > 0) {
            modifyItem(patch);
        }
    }, [cached, item.item_type_details, item.assembly_type]);

    //total_cubes
    useEffect(() => {
        if (((item.length && item.width && item.height) || item.cubes_per_unit) && item.quantity) {
            modifyItem({
                total_cubes:
                    item.quantity *
                    (item.cubes_per_unit
                        ? item.cubes_per_unit
                        : (item.length * item.width * item.height) / (12 * 12 * 12)),
            });
        }
    }, [item.length, item.width, item.height, item.quantity, item.cubes_per_unit]);

    //total_weight
    useEffect(() => {
        if (item.weight && item.quantity) {
            modifyItem({ total_weight: item.quantity * item.weight });
        }
    }, [item.weight, item.quantity]);

    const getDimensionsDebounced = useMemo(
        () =>
            _.debounce((payload) => {
                return getDimensions(payload);
            }, 250),
        []
    );

    //cached_sku
    useEffect(() => {
        if (!!item?.sku) {
            getDimensionsDebounced({
                variables: {
                    sku: item.sku,
                    client_id: shipperId,
                },
            });
        }
    }, [getDimensionsDebounced, item.sku, shipperId]);

    // The effects above only gap-fill; callers need the row itself to overwrite on an explicit pick.
    return { catalogRow: cached?.result?.[0] || null };
};
